import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { FaTimes, FaSave, FaUserPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import StudentService from '../../../services/studentService';

const AddStudentModal = ({ show, onHide, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generatedPassword, setGeneratedPassword] = useState('');

  // Generate password from name
  const generatePassword = (name) => {
    if (!name) return '';
    // const firstName = name.trim().split(' ')[0].toLowerCase();
    return '123456';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Generate password preview when name changes
    if (name === 'name') {
      setGeneratedPassword(generatePassword(value));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const password = generatePassword(formData.name);
      
      // Prepare data for API - using the new endpoint
      const studentData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
        // No course_id, no password sent - backend generates it
      };

      // Use the new method
      const response = await StudentService.createStudentOnly(studentData);
      
      // Show success message with generated password from backend
      toast.success(
        <div>
          <strong>Student added successfully!</strong><br />
          Password: <strong>{response.data.password || password}</strong>
        </div>,
        { autoClose: 8000 }
      );
      
      onSuccess();
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: ''
      });
      setGeneratedPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add student');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when modal closes
    setFormData({
      name: '',
      email: '',
      phone: ''
    });
    setGeneratedPassword('');
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>
          <FaUserPlus className="me-2" />
          Add New Student
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter student name"
              isInvalid={!!errors.name}
              disabled={loading}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              isInvalid={!!errors.email}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter phone number"
              isInvalid={!!errors.phone}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">
              {errors.phone}
            </Form.Control.Feedback>
          </Form.Group>

          {generatedPassword && (
            <Alert variant="info" className="mt-3">
              <strong>Auto-generated Password:</strong> {generatedPassword}
              <br />
              <small>Student will use this password to login. Role: Student, Status: Approved</small>
            </Alert>
          )}

          <Alert variant="light" className="mt-2">
            <small>
              <strong>Note:</strong> Student will be created in system only. 
              They can enroll in courses later from student dashboard.
            </small>
          </Alert>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Adding...
              </>
            ) : (
              <>
                <FaSave className="me-2" /> Add Student
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddStudentModal;